import {
  Alert,
  Button,
  Container,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Icon,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  MenuItem,
  Spinner,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Stack } from '@chakra-ui/layout';
import { useForm } from 'react-hook-form';
import { FiFile } from 'react-icons/all';
import { FiX } from 'react-icons/fi';
import { Heading, Input } from '~/components';
import { GeneralInformation } from '~/pages/Portal/Sinisters/GeneralInformation';
import { useSinister } from '~/hooks/useSinisters/useSinisters';
import apiSinister from '~/utils/apiSinister';
import { queryClient } from '~/utils/react-query';

export const SinistersProvePaymentForm: React.FC<{
  sinisterId: number;
  link?: boolean;
}> = ({ sinisterId, link = false }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef(null);
  const toast = useToast();
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
  } = useForm();
  const { errors } = formState;
  const [isFetching, setFetching] = useState(false);

  const comprovanteRef = useRef<HTMLInputElement | null>(null);

  const [comprovante, setSelectedComprovante] = useState<any>();
  const [nomeComprovante, setNomeComprovante] = useState<string | undefined>(
    '',
  );
  const { data, isLoading, refetch } = useSinister(sinisterId);

  useEffect(() => {
    if (link) handleOpen();
  }, []);

  const handleOpen = () => {
    onOpen();
  };

  const handleClose = () => {
    clearValues();
    onClose();
  };
  const clearValues = () => {
    setNomeComprovante('');
    setSelectedComprovante(null);
    clearErrors();
  };

  async function handleCreate() {
    clearErrors();

    !(comprovante || nomeComprovante) &&
      setError('comprovante', {
        type: 'required',
        message: 'Arquivo é obrigatório',
      });

    const { _formState } = control;
    if (!_formState.isValid) return;

    const form = new FormData();
    if (comprovante) {
      form.append('comprovante_pagamento', comprovante);
    }

    setFetching(true);

    const request = apiSinister.put(`/comprovar-pagamento/${data?.id}/`, form);

    request
      .then(() => {
        toast({
          title: `Comprovante de pagamento enviado`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        clearValues();
        onClose();
        queryClient.invalidateQueries('sinistersList');
        queryClient.invalidateQueries(['sinisterLog', sinisterId]);
        queryClient.invalidateQueries(['sinister', sinisterId]);
        queryClient.invalidateQueries('sinisterDashboard');
        queryClient.invalidateQueries(['sinisterDocuments', sinisterId]);
      })
      .catch(error => {
        if (error.response.status === 403) {
          toast({
            title: 'Usuário sem permissão',
            description: 'Sem permissão para execução da operação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 413) {
          toast({
            title: `Tamanho de requisição excedida`,
            description: 'Limite máximo para tamanho de arquivos é de 5MB',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 400) {
          // erros de valid

          const errorsValidate = Object.entries(error.response.data);

          errorsValidate.forEach(err => {
            toast({
              title: `Erros ao enviar comprovante de pagamento`,
              description: String(err[1]),
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
        } else {
          toast({
            title: `Erros ao enviar comprovante de pagamento`,
            description: 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }
  const handleComprovanteChange = (_file: File | null) => {
    setSelectedComprovante(_file);
    setNomeComprovante(_file ? _file.name : '');
    clearErrors('comprovante');
  };
  const clearComprovante = () => {
    setSelectedComprovante(null);
    setNomeComprovante('');
    setValue('comprovante', null);
  };

  return (
    <>
      <MenuItem onClick={handleOpen}>Comprovar pagamento</MenuItem>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size="lg"
        closeOnOverlayClick={!isFetching}
      >
        <DrawerOverlay />
        <DrawerContent borderTopLeftRadius="md">
          <DrawerCloseButton isDisabled={isFetching} />
          <DrawerHeader>Comprovar pagamento</DrawerHeader>
          <DrawerBody>
            <Stack gridGap={6} bg="white" maxW={600} w="100%">
              <Stack gridGap={4}>
                {data && data.revisao_comentario && (
                  <Alert
                    bg="rgba(255, 215, 71, 0.4);"
                    color="#88460D"
                    display="block"
                    fontSize="16px"
                    borderRadius={10}
                  >
                    <Heading color="#88460D" fontSize="18px" marginBottom={3}>
                      O que deve ser corrigido?
                    </Heading>
                    {data?.revisao_comentario}
                  </Alert>
                )}
                <GeneralInformation sinister={data!} />
                <Heading>Enviar comprovante de pagamento</Heading>
                {/* comprovante */}
                <VStack>
                  <Container
                    maxW="container.lg"
                    bg="gray.100"
                    padding="1rem 1rem 1rem 1rem"
                    borderRadius="5px"
                    borderColor="gray.300"
                    borderWidth="1px"
                    width="70%"
                    marginStart="0px"
                  >
                    <FormControl isInvalid={errors.comprovante}>
                      <FormLabel
                        htmlFor="writeUpFile"
                        style={{ fontSize: '14px' }}
                      >
                        Upload do comprovante *
                      </FormLabel>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <Icon as={FiFile} />
                        </InputLeftElement>
                        {(comprovante || nomeComprovante) && (
                          <InputRightElement pointerEvents="auto">
                            <Icon
                              as={FiX}
                              style={{
                                cursor: 'pointer',
                                pointerEvents: isFetching ? 'none' : 'auto',
                              }}
                              onClick={clearComprovante}
                            />
                          </InputRightElement>
                        )}
                        <input
                          id="file"
                          type="file"
                          onChange={e => {
                            handleComprovanteChange(
                              e.target.files ? e.target.files[0] : null,
                            );
                            e.target.value = '';
                          }}
                          accept=".pdf, image/png, .jpg"
                          ref={comprovanteRef}
                          style={{ display: 'none' }}
                        />
                        <Input
                          placeholder="Selecione um arquivo"
                          onClick={() =>
                            comprovanteRef.current
                              ? comprovanteRef.current.click()
                              : ''
                          }
                          value={nomeComprovante || ''}
                          name="boletim"
                          style={{ paddingLeft: '40px', paddingRight: '40px' }}
                          isReadOnly
                          backgroundColor="white"
                          error={errors.comprovante}
                          isDisabled={isFetching}
                        />
                      </InputGroup>
                    </FormControl>
                  </Container>
                </VStack>
              </Stack>
            </Stack>
            <Button
              type="submit"
              onClick={handleSubmit(handleCreate)}
              color="white"
              background="gray.900"
              _hover={{
                bgColor: 'black',
              }}
              style={{ marginTop: '20px' }}
              isDisabled={isFetching}
            >
              Enviar {isFetching && <Spinner style={{ marginLeft: '10px' }} />}
            </Button>
            <Button
              type="submit"
              onClick={handleClose}
              color="black"
              background="transparent"
              style={{
                marginTop: '20px',
                marginLeft: '10px',
              }}
              isDisabled={isFetching}
            >
              Cancelar
            </Button>
            <Text
              style={{ fontSize: '12px', color: 'gray.500', marginTop: '20px' }}
            >
              * Campos obrigatórios
            </Text>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
